<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    modelValue: {
        type: [Number, String],
        default: 0
    },
    label: {
        type: String,
        default: ''
    },
    errorMessages: {
        type: [String, Array],
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    align: {
        type: String,
        default: 'right'
    },
    editable: {
        type: Boolean,
        default: true
    },
    mode: {
        type: String,
        default: 'edit'
    }
});

const emit = defineEmits(['update:modelValue']);
const localError = ref('');
const isEditing = ref(false);

const formattedValue = computed({
    get() {
        if (isEditing.value && props.editable && props.mode === 'edit') {
            const value = Number(props.modelValue);
            return isNaN(value) ? '' : value.toString();
        }
        const value = Number(props.modelValue);
        if (isNaN(value)) return '';
        return (
            value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }) + ' %'
        );
    },
    set(newValue) {
        // Remove any non-numeric characters except decimal point
        const cleanValue = newValue.replace(/[^\d.-]/g, '');
        const numericValue = parseFloat(cleanValue);

        // Clear error if input is empty
        if (!cleanValue) {
            localError.value = '';
            emit('update:modelValue', 0);
            return;
        }

        // Validate numeric value
        if (isNaN(numericValue)) {
            localError.value = 'Please enter a valid number';
            return;
        }

        // Validate range
        if (numericValue < 0) {
            localError.value = 'Percentage cannot be negative';
            return;
        }
        if (numericValue > 100) {
            localError.value = 'Percentage cannot exceed 100%';
            return;
        }

        // Clear error and emit valid value
        localError.value = '';
        emit('update:modelValue', numericValue);
    }
});

const handleFocus = () => {
    if (props.editable && props.mode === 'edit') {
        isEditing.value = true;
    }
};

const handleBlur = () => {
    isEditing.value = false;
};
</script>

<template>
    <v-text-field
        v-model="formattedValue"
        :label="label"
        :error-messages="localError || errorMessages"
        :disabled="disabled"
        :required="required"
        density="compact"
        :class="[
            { 'text-right': align === 'right' },
            { 'no-focus': !editable || mode === 'view' },
            'number-field'
        ]"
        hide-details="auto"
        variant="plain"
        :readonly="editable ? (mode === 'edit' ? false : true) : true"
        clearable="false"
        class="no-clear-button"
        @focus="handleFocus"
        @blur="handleBlur"
        type="text"
        inputmode="decimal"
        :tabindex="!editable || mode === 'view' ? -1 : undefined"
    />
</template>

<style scoped>
.no-focus {
    pointer-events: none;
}

.no-focus :deep(.v-field__input) {
    cursor: default !important;
}

:deep(.v-field__clearable) {
    display: none !important;
}

.no-clear-button :deep(.v-field__append-inner) {
    display: none !important;
}
.number-field {
    width: 100%;
}

.number-field input {
    padding: 0 !important;
    text-align: right;
}

.number-field .v-field__append-inner,
.number-field .v-field__append,
.number-field .v-input__append,
.number-field .v-field__clearable {
    display: none !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 0 !important;
}

.number-field .v-field__field {
    padding: 0 !important;
}

.number-field .v-field__input {
    padding-right: 0 !important;
    min-height: var(--v-input-control-height) !important;
}

.number-field .v-field {
    padding: 0 !important;
}
</style>
